import React from 'react';
import CaliberFilter from '../filters/CaliberFilter';
import VarietyFilter from '../filters/VarietyFilter';
import FormatFilter from '../filters/FormatFilter';
import RegionFilter from '../filters/RegionFilter';
import ColorFilter from '../filters/ColorFilter';
import FleetFilter from '../filters/FleetFilter';
import { MarketData } from 'common/src/utils/types';

export type FilterType = 'caliber' | 'variety' | 'format' | 'market' | 'color' | 'fleet';

interface EvolutionChartFiltersProps {
  // Primary filter type (defines the chart lines)
  primaryFilter: FilterType;

  // Data source
  data: MarketData[];

  // Filter states and handlers
  selectedCalibers: string[];
  onCalibersChange: (value: string[]) => void;

  selectedVarieties: string[];
  onVarietiesChange: (value: string[]) => void;

  selectedFormats: string[];
  onFormatsChange: (value: string[]) => void;

  selectedMarkets: string[];
  onMarketsChange: (value: string[]) => void;

  selectedColors: string[];
  onColorsChange: (value: string[]) => void;

  selectedFleets: string[];
  onFleetsChange: (value: string[]) => void;

  // Optional config
  hasColor?: boolean;
}

const EvolutionChartFilters: React.FC<EvolutionChartFiltersProps> = ({
  primaryFilter,
  data,
  selectedCalibers,
  onCalibersChange,
  selectedVarieties,
  onVarietiesChange,
  selectedFormats,
  onFormatsChange,
  selectedMarkets,
  onMarketsChange,
  selectedColors,
  onColorsChange,
  selectedFleets,
  onFleetsChange,
  hasColor = false,
}) => {
  // Helper to check if a filter is the primary one
  const isPrimary = (filter: FilterType) => filter === primaryFilter;

  // Render a single filter based on type
  const renderFilter = (type: FilterType, highlighted: boolean = false) => {
    const commonProps = {
      data,
      selectionMode: 'multiple' as const,
      className: highlighted ? 'min-w-40 ring-2 ring-blue-500' : 'min-w-40',
      size: 'sm' as const,
    };

    switch (type) {
      case 'caliber':
        return (
          <CaliberFilter
            {...commonProps}
            selectedCaliber={selectedCalibers}
            onSelectionChange={onCalibersChange}
          />
        );
      case 'variety':
        return (
          <VarietyFilter
            {...commonProps}
            selectedVariety={selectedVarieties}
            onSelectionChange={(keys) => onVarietiesChange(Array.from(keys) as string[])}
            allowAllVarieties={true}
            defaultSelectAll={!isPrimary('variety')}
          />
        );
      case 'format':
        return (
          <FormatFilter
            {...commonProps}
            selectedFormat={selectedFormats}
            onSelectionChange={onFormatsChange}
          />
        );
      case 'market':
        return (
          <RegionFilter
            {...commonProps}
            selectedRegion={selectedMarkets}
            onSelectionChange={onMarketsChange}
          />
        );
      case 'color':
        return hasColor ? (
          <ColorFilter
            {...commonProps}
            selectedColor={selectedColors}
            onSelectionChange={onColorsChange}
            emptyMeansAll
          />
        ) : null;
      case 'fleet':
        return (
          <FleetFilter
            {...commonProps}
            data={data}
            selectedFleet={selectedFleets}
            onSelectionChange={onFleetsChange}
          />
        );
      default:
        return null;
    }
  };

  // Define the order of filters (excluding primary)
  const allFilterTypes: FilterType[] = ['caliber', 'variety', 'format', 'market', 'color', 'fleet'];
  const secondaryFilters = allFilterTypes.filter(f => f !== primaryFilter);

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {/* Primary filter first - highlighted */}
      {renderFilter(primaryFilter, true)}

      {/* Secondary filters */}
      {secondaryFilters.map(filterType => (
        <React.Fragment key={filterType}>
          {renderFilter(filterType)}
        </React.Fragment>
      ))}
    </div>
  );
};

export default React.memo(EvolutionChartFilters);
